/**
 * Open-shift claims and swaps in the planner (#2040): the open requests of the visible
 * range and the group lead's or admin's decision.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { ShiftRequestsService } from '@/api/services/ShiftRequestsService';
import type { shift_requests_ShiftRequestResponse } from '@/api/models/shift_requests_ShiftRequestResponse';
import { errorToastText, extractBackendError } from '@/lib/translateBackendError';
import { shiftKeys } from './useShifts';

export type ShiftRequest = shift_requests_ShiftRequestResponse;

export const shiftRequestKeys = {
  all: ['shift-requests'] as const,
  range: (fromDate: string, toDate: string) => [...shiftRequestKeys.all, 'range', fromDate, toDate] as const,
};

/** The pending and accepted requests on the shifts of a range. */
export function useShiftRequests(fromDate: string, toDate: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: shiftRequestKeys.range(fromDate, toDate),
    queryFn: () => ShiftRequestsService.getApiV1ShiftRequests(fromDate, toDate),
    enabled: (options?.enabled ?? true) && !!fromDate && !!toDate && isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

const decisionErrorFallback = msg({ id: 'shiftRequests.toast.error', message: 'The request could not be decided' });
// In a decision the overlap and the absence are the employee's who would get the shift.
export const decisionConflict = msg({
  id: 'shiftRequests.error.decisionConflict',
  message: 'The employee who would get the shift already has a shift at this time.',
});
const decisionAbsent = msg({
  id: 'shiftRequests.error.decisionAbsent',
  message: 'The employee who would get the shift is absent on this day.',
});

/** Approve or reject one request; the planner and the requests reload afterwards. */
export function useDecideShiftRequest() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  return useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; approve: boolean; reason?: string }) =>
      approve
        ? ShiftRequestsService.postApiV1ShiftRequestsApprove(id, reason ? { reason } : undefined)
        : ShiftRequestsService.postApiV1ShiftRequestsReject(id, reason ? { reason } : undefined),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: shiftRequestKeys.all });
      void queryClient.invalidateQueries({ queryKey: shiftKeys.all });
      toast.success(
        variables.approve
          ? i18n._({ id: 'shiftRequests.toast.approved', message: 'Approved. The employee was notified.' })
          : i18n._({ id: 'shiftRequests.toast.rejected', message: 'Rejected. The employee was notified.' })
      );
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: shiftRequestKeys.all });
      const { code } = extractBackendError(error);
      if (code === 'SHIFT_CONFLICT') {
        toast.error(i18n._(decisionConflict));
      } else if (code === 'EMPLOYEE_ABSENT') {
        toast.error(i18n._(decisionAbsent));
      } else {
        toast.error(errorToastText(error, i18n, decisionErrorFallback));
      }
    },
  });
}
